import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Form,
  Input,
  InputNumber,
  Row,
  Select,
  Space,
  Spin,
  Switch,
  Typography,
  message,
} from 'antd';
import { ReloadOutlined, SaveOutlined } from '@ant-design/icons';
import {
  createDefaultStaffAutoDispatchConfig,
  getStaffAutoDispatchConfig,
  saveStaffAutoDispatchConfig,
  type StaffAutoDispatchConfig,
} from '../../services/staffAutoDispatchConfigService';

const { Title, Paragraph, Text } = Typography;

const strategyOptions = [
  { label: '综合均衡', value: 'balanced' },
  { label: '距离优先', value: 'distance_first' },
  { label: '单量优先', value: 'order_count_first' },
];

const StaffAutoDispatchConfigPage: React.FC = () => {
  const [form] = Form.useForm<StaffAutoDispatchConfig>();
  const [initialLoading, setInitialLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadConfig = useCallback(async () => {
    setInitialLoading(true);
    try {
      const data = await getStaffAutoDispatchConfig();
      form.setFieldsValue(data);
    } catch {
      form.setFieldsValue(createDefaultStaffAutoDispatchConfig());
      message.error('加载配置失败');
    } finally {
      setInitialLoading(false);
    }
  }, [form]);

  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  const handleSubmit = async (values: StaffAutoDispatchConfig) => {
    setSubmitting(true);
    try {
      const saved = await saveStaffAutoDispatchConfig(values);
      form.setFieldsValue(saved);
      message.success('保存成功');
    } catch {
      message.error('保存配置失败');
    } finally {
      setSubmitting(false);
    }
  };

  const handleReset = async () => {
    const defaults = createDefaultStaffAutoDispatchConfig();
    setSubmitting(true);
    try {
      const saved = await saveStaffAutoDispatchConfig(defaults);
      form.setFieldsValue(saved);
      message.success('已恢复默认配置');
    } catch {
      message.error('恢复默认配置失败');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <Title level={2}>自动派单配置</Title>
        <Paragraph type="secondary" style={{ marginBottom: 0 }}>
          配置服务人员自动派单的启用状态、优先级策略和兜底规则。
        </Paragraph>
      </div>

      <div className="page-content">
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 24 }}
          title="当前页面用于后台配置自动派单规则"
          description="配置保存后会写入数据库。后台管理端刷新后会读取最新配置，后续服务端也可以直接基于这份配置执行派单逻辑。"
        />

        <Spin spinning={initialLoading}>
          <Form
            form={form}
            layout="vertical"
            onFinish={handleSubmit}
            initialValues={createDefaultStaffAutoDispatchConfig()}
          >
            <Card title="基础策略" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} md={12}>
                  <Form.Item name="enabled" label="启用自动派单" valuePropName="checked">
                    <Switch checkedChildren="已启用" unCheckedChildren="已关闭" />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="strategy"
                    label="派单策略"
                    rules={[{ required: true, message: '请选择派单策略' }]}
                  >
                    <Select options={strategyOptions} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="maxDispatchDistanceKm"
                    label="最大派单距离（公里）"
                    rules={[{ required: true, message: '请输入最大派单距离' }]}
                  >
                    <InputNumber min={1} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="responseTimeoutMinutes"
                    label="接单响应时限（分钟）"
                    rules={[{ required: true, message: '请输入接单响应时限' }]}
                  >
                    <InputNumber min={1} max={60} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="maxRetryCount"
                    label="派单失败重试次数"
                    rules={[{ required: true, message: '请输入重试次数' }]}
                  >
                    <InputNumber min={0} max={10} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
                <Col xs={24} md={12}>
                  <Form.Item
                    name="dailyDispatchLimit"
                    label="单人每日派单上限"
                    rules={[{ required: true, message: '请输入每日派单上限' }]}
                  >
                    <InputNumber min={1} max={999} style={{ width: '100%' }} />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="人员筛选规则" style={{ marginBottom: 24 }}>
              <Row gutter={16}>
                <Col xs={24} md={8}>
                  <Form.Item name="requireCertifiedStaff" label="仅认证服务人员" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="onlyOnlineStaff" label="仅在线服务人员" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="allowCrossRegion" label="允许跨区域派单" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="autoAcceptAfterDispatch" label="派单后自动确认接单" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="respectServiceTimeWindow" label="校验可接单时段" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
                <Col xs={24} md={8}>
                  <Form.Item name="fallbackToManual" label="失败后转人工派单" valuePropName="checked">
                    <Switch />
                  </Form.Item>
                </Col>
              </Row>
            </Card>

            <Card title="派单说明" style={{ marginBottom: 24 }}>
              <Form.Item
                name="dispatchRemark"
                label="规则备注"
                rules={[{ required: true, message: '请输入派单说明' }]}
              >
                <Input.TextArea
                  rows={4}
                  placeholder="填写自动派单执行规则、适用范围或业务说明"
                  maxLength={200}
                  showCount
                />
              </Form.Item>
              <Space orientation="vertical" size={4}>
                <Text type="secondary">建议说明距离、单量、在线状态和失败兜底逻辑。</Text>
                <Text type="secondary">这份配置会作为前后端统一使用的自动派单参数来源。</Text>
              </Space>
            </Card>

            <Card>
              <Space>
                <Button type="primary" icon={<SaveOutlined />} loading={submitting} onClick={() => form.submit()}>
                  保存配置
                </Button>
                <Button icon={<ReloadOutlined />} loading={submitting} onClick={handleReset}>
                  恢复默认
                </Button>
              </Space>
            </Card>
          </Form>
        </Spin>
      </div>
    </div>
  );
};

export default StaffAutoDispatchConfigPage;
